import { useQuery } from '@apollo/client/react';
import React, { useEffect, useRef } from 'react';
import { useMatch } from 'react-router';
import {
  CoachType,
  MemberChatBasicsDocument,
  SelfDocument,
} from '../../graphql/types';
import { notEmpty } from '../../utils/notEmpty';
import { Chat } from '.';
import { ChatSize, useChatContext } from './Context';

const MEMBER_ROUTE = '/app/members/:memberId/*';

export const ChatController: React.FC = () => {
  const memberMatch = useMatch(MEMBER_ROUTE);
  const memberId = memberMatch?.params?.memberId;

  const {
    openChat,
    closeChat,
    chatSize,
    setChatSize,
    setCloseable,
    member: currentChatMember,
  } = useChatContext();

  const { data: selfData } = useQuery(SelfDocument, {
    fetchPolicy: 'cache-first',
  });
  const coachId = selfData?.self?.id;
  const isCalendarOnly =
    selfData?.self?.coachData?.type === CoachType.CalendarOnly;

  const { data: memberData, loading: memberIsLoading } = useQuery(
    MemberChatBasicsDocument,
    {
      variables: {
        memberId: memberId ?? '',
        chatUserIds: [coachId, memberId].filter(notEmpty),
      },
      skip: !memberId || !coachId,
    },
  );

  const member = memberData?.user;
  const chatId = memberData?.chatRoom?.id;
  const memberHasSignedIn =
    memberId && typeof member?.firstSignedInAt === 'string';
  const memberHasChanged = memberId !== currentChatMember?.id;

  const prevMemberIdRef = useRef(memberId);

  // biome-ignore lint/correctness/useExhaustiveDependencies: chatSize is read at navigation time but must not retrigger — only leaving a member route should close a minimized chat
  useEffect(() => {
    const prevMemberId = prevMemberIdRef.current;
    prevMemberIdRef.current = memberId;

    // close chat when navigating away from member profile if it was minimized
    if (!memberId && prevMemberId && chatSize === ChatSize.Minimized) {
      closeChat();
    }
  }, [memberId, closeChat]);

  useEffect(() => {
    if (memberId) {
      // on a member-specific route, render non-closeable chat focused on the member
      setCloseable(false);
    } else {
      // not on a member-specific route, render closeable chat/chat list
      setCloseable(true);
    }
  }, [memberId, setCloseable]);

  useEffect(() => {
    if (!memberId || !member || memberIsLoading || !memberHasSignedIn) {
      return;
    }

    openChat({ member, chatId, updateChatSize: false });
    setChatSize((prev) => {
      if (memberHasChanged) {
        return ChatSize.Minimized;
      }
      if (prev === ChatSize.Closed) {
        // if chat was closed, open it minimized
        return ChatSize.Minimized;
      }
      return prev;
    });
  }, [
    memberId,
    chatId,
    memberIsLoading,
    member,
    openChat,
    setChatSize,
    memberHasSignedIn,
    memberHasChanged,
  ]);

  if (!selfData?.self || isCalendarOnly || (memberId && !memberHasSignedIn)) {
    return null;
  }

  return <Chat />;
};
